<template>
  <div
    ref="root"
    class="vs-demo"
    :class="{ dark: theme === 'dark', 'vs-demo--expanded': expanded }"
    :style="brandStyle"
    :role="expanded ? 'dialog' : 'region'"
    :aria-modal="expanded || undefined"
    :aria-label="`Интерактивное демо интерфейса ${appName}`"
    @keydown.esc="closeExpanded"
  >
    <aside class="app-sidebar" aria-label="Навигация демо">
      <button
        class="app-sidebar__logo"
        type="button"
        :aria-label="`На главную демо ${appName}`"
        @click="navigate('cameras')"
      >
        <img
          v-if="logoUrl"
          class="demo-brand-logo"
          :class="{ 'demo-brand-logo--wordmark': logoIsWordmark }"
          :src="logoUrl"
          :alt="`Логотип ${appName}`"
        />
        <span v-else-if="whiteLabel" class="demo-brand-monogram" aria-hidden="true">{{
          initials
        }}</span>
        <DemoLogo v-else :size="32" fill="currentColor" />
        <span v-if="!logoIsWordmark" class="demo-brand-name" :title="appName">{{ appName }}</span>
      </button>
      <nav class="app-sidebar__nav">
        <button
          v-for="item in navigation"
          :key="item.id"
          type="button"
          class="app-sidebar__link"
          :class="{ 'app-sidebar__link--active': page === item.id }"
          :aria-current="page === item.id ? 'page' : undefined"
          :aria-label="item.label"
          :title="item.label"
          @click="navigate(item.id)"
        >
          <DemoIcon class="app-sidebar__icon" :name="item.icon" /><span
            class="app-sidebar__label"
            >{{ item.label }}</span
          >
        </button>
      </nav>
      <hr class="app-sidebar__divider" />
      <div class="demo-camera-tree">
        <span class="demo-tree-caption">КАМЕРЫ</span
        ><button type="button" :class="{ selected: !focused }" @click="showAll">
          <DemoIcon name="camera" />Все камеры <small>2</small></button
        ><button
          v-for="camera in cameras"
          :key="camera.id"
          type="button"
          :class="{ selected: focused === camera.id }"
          @click="openCamera(camera.id)"
        >
          <i class="demo-status-dot" :class="{ 'is-offline': !camera.enabled }" /><span>{{
            camera.name
          }}</span>
        </button>
      </div>
      <div class="demo-sidebar-note"><span class="demo-status-dot" />Демонстрационный объект</div>
    </aside>
    <div class="demo-app-body">
      <header class="demo-app-header">
        <span class="demo-app-name"
          >{{ appName }} <span>/ {{ pageTitle }}</span></span
        >
        <div>
          <span class="demo-badge-label">ДЕМО · 30 СЕКУНД</span
          ><button
            type="button"
            class="demo-icon-button"
            :aria-label="
              theme === 'dark' ? 'Включить светлую тему демо' : 'Включить тёмную тему демо'
            "
            :title="theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'"
            @click="theme = theme === 'dark' ? 'light' : 'dark'"
          >
            <DemoIcon :name="theme === 'dark' ? 'sun' : 'moon'" /></button
          ><button
            v-if="expanded || fullscreen"
            ref="closeButton"
            type="button"
            class="demo-icon-button"
            aria-label="Закрыть полноэкранный режим демо"
            @click="closeExpanded"
          >
            <DemoIcon name="close" />
          </button>
        </div>
      </header>
      <div v-if="page === 'settings'" class="demo-settings">
        <div class="demo-panel-heading">
          <h3>Настройки</h3>
          <p>Попробуйте настройки интерфейса и камер.</p>
        </div>
        <section class="demo-settings-card">
          <h4>Интерфейс</h4>
          <label
            >Тема<select v-model="theme" aria-label="Тема демо">
              <option value="dark">Тёмная</option>
              <option value="light">Светлая</option>
            </select></label
          ><label
            >Изображение<select v-model="fit" aria-label="Масштаб видео">
              <option value="contain">Вписать целиком</option>
              <option value="cover">Заполнить окно</option>
            </select></label
          >
        </section>
        <section class="demo-settings-card">
          <h4>Камеры</h4>
          <div v-for="camera in cameras" :key="camera.id" class="demo-camera-setting">
            <label :for="`demo-name-${camera.id}`"
              >Название камеры {{ camera.id
              }}<input
                :id="`demo-name-${camera.id}`"
                v-model="camera.name"
                maxlength="36"
                @blur="restoreName(camera)" /></label
            ><label class="demo-toggle-label"
              ><input
                v-model="camera.enabled"
                type="checkbox"
                :aria-label="`Включить трансляцию камеры ${camera.id}`"
              />Трансляция</label
            >
          </div>
        </section>
        <button type="button" class="demo-action" @click="resetSettings">
          <DemoIcon name="reset" />Сбросить настройки демо
        </button>
      </div>
      <div v-else-if="page === 'recordings' && !archiveOpen" class="demo-recordings">
        <div class="demo-panel-heading">
          <h3>Записи</h3>
          <p>Выберите день, чтобы открыть архив камер.</p>
        </div>
        <button type="button" class="demo-folder" @click="openArchive">
          <DemoIcon name="folder" /><strong>10 октября 2026</strong
          ><span>2 камеры · 14:32:00–14:32:30</span
          ><span class="demo-folder-previews"
            ><img
              v-for="camera in cameras"
              :key="camera.id"
              :src="camera.poster"
              :alt="camera.name"
              loading="lazy" /></span
          ><span class="demo-folder-open">Открыть записи</span>
        </button>
      </div>
      <template v-else>
        <div v-if="focused || page === 'recordings'" class="demo-detail-toolbar">
          <button
            type="button"
            class="demo-action"
            @click="focused ? showAll() : (archiveOpen = false)"
          >
            <DemoIcon name="back" />{{ focused ? 'Все камеры' : 'К папкам' }}</button
          ><strong>{{ focusedCamera?.name || '10 октября 2026' }}</strong>
          <div v-if="page !== 'recordings'" class="demo-mode-toggle" aria-label="Режим просмотра">
            <button
              type="button"
              :class="{ active: mode === 'live' }"
              :aria-pressed="mode === 'live'"
              @click="goLive"
            >
              Обзор</button
            ><button
              type="button"
              :class="{ active: mode === 'archive' }"
              :aria-pressed="mode === 'archive'"
              @click="mode = 'archive'"
            >
              Архив
            </button>
          </div>
        </div>
        <DemoToolbar
          v-else
          :view-mode="viewMode"
          :groups="groups"
          selected-group-id="all"
          :filter-group-ids="groupIds"
          :filter="filter"
          :grid-columns="columns"
          :selection-mode="selectionMode"
          :selection-draft-count="selectedIds.length"
          :selection-applied="selectionApplied"
          site-label="Выбранная группа"
          @update:view-mode="viewMode = $event"
          @update:filter-group-ids="groupIds = $event"
          @update:filter="filter = $event"
          @update:grid-columns="columns = $event"
          @update:selection-mode="setSelectionMode"
          @apply-selection="applySelection"
          @clear-selection-view="clearSelection"
          @fullscreen="toggleFullscreen"
        />
        <div class="demo-view-summary">
          <span
            ><i class="demo-status-dot" />{{ mode === 'archive' ? 'Архив' : 'Обзор' }}
            <span class="demo-summary-divider">/</span> {{ displayed.length }}
            {{ displayed.length === 1 ? 'камера' : 'камеры' }}</span
          ><span>{{
            selectionMode
              ? 'Нажмите на камеры для выбора'
              : 'Выберите камеру для подробного просмотра'
          }}</span>
        </div>
        <div class="demo-view-area">
          <div v-if="!displayed.length" class="demo-empty">
            <DemoIcon name="camera" />
            <h4>Нет камер с такими условиями</h4>
            <button type="button" class="demo-action" @click="showAll">Показать все камеры</button>
          </div>
          <div v-else-if="viewMode === 'list' && !focused" class="demo-camera-list">
            <div class="demo-list-head">
              <span>Камера</span><span>Состояние</span><span>Просмотр</span>
            </div>
            <article v-for="camera in displayed" :key="camera.id">
              <button type="button" class="demo-list-camera" @click="selectCamera(camera.id)">
                <img :src="camera.poster" :alt="camera.name" /><span
                  >{{ camera.name }}<small>{{ camera.groupLabel }}</small></span
                ></button
              ><span class="demo-list-status"
                ><i class="demo-status-dot" :class="{ 'is-offline': !camera.enabled }" />{{
                  camera.enabled ? 'Подключена' : 'Отключена'
                }}</span
              ><button
                type="button"
                class="demo-action"
                @click="selectionMode ? selectCamera(camera.id) : openCamera(camera.id)"
              >
                {{
                  selectionMode
                    ? selectedIds.includes(camera.id)
                      ? 'Выбрано'
                      : 'Выбрать'
                    : 'Открыть'
                }}
              </button>
            </article>
          </div>
          <div v-else class="demo-camera-grid" :style="{ '--demo-columns': focused ? 1 : columns }">
            <article
              v-for="camera in displayed"
              :key="camera.id"
              class="camera-tile"
              :class="{
                'camera-tile--selected': selectionMode && selectedIds.includes(camera.id),
                'camera-tile--focused': !!focused,
              }"
            >
              <div class="camera-tile__header">
                <div class="camera-tile__label">
                  <i
                    class="demo-status-dot"
                    :class="{ 'is-offline': !camera.enabled && mode !== 'archive' }"
                  /><span>{{ camera.name }}</span>
                </div>
                <span class="demo-tile-group">{{ camera.groupLabel }}</span>
              </div>
              <DemoPlayer
                :ref="(el) => setPlayerRef(camera.id, el)"
                :src="camera.src"
                :poster="camera.poster"
                :name="camera.name"
                :enabled="camera.enabled || mode === 'archive'"
                :load="loaded"
                :playing="shouldPlay"
                :loop="mode === 'live'"
                :speed="speed"
                :time="time"
                :seek-version="seekVersion"
                :timestamp="timestamp"
                :style="{ '--demo-video-fit': fit }"
                @time="onTime(camera.id, $event)"
                @ended="onEnded(camera.id)"
                @resume="playing = true"
                @notice="notice = $event"
              />
              <button
                v-if="!focused"
                type="button"
                class="demo-tile-open"
                :aria-label="
                  selectionMode ? `Выбрать камеру ${camera.name}` : `Открыть камеру ${camera.name}`
                "
                :aria-pressed="selectionMode ? selectedIds.includes(camera.id) : undefined"
                @click="selectCamera(camera.id)"
              >
                <span v-if="selectionMode" class="demo-selection-check">{{
                  selectedIds.includes(camera.id) ? '✓' : '+'
                }}</span>
              </button>
              <div class="demo-tile-actions">
                <button
                  type="button"
                  class="demo-video-button"
                  :aria-label="`Сохранить кадр: ${camera.name}`"
                  title="Сохранить кадр"
                  @click="playerRefs[camera.id]?.snapshot()"
                >
                  <DemoIcon name="snapshot" /></button
                ><button
                  type="button"
                  class="demo-video-button"
                  :aria-label="`Развернуть камеру ${camera.name}`"
                  title="Развернуть"
                  @click="expandCamera(camera.id)"
                >
                  <DemoIcon name="expand" />
                </button>
              </div>
            </article>
          </div>
          <div v-if="focused" class="demo-camera-footer">
            <span
              >{{
                mode === 'archive' ? 'Запись от 10 октября 2026' : 'Демонстрационный видеопоток'
              }}
              · {{ timestamp }}</span
            ><button type="button" class="demo-action" @click="playerRefs[focused]?.snapshot()">
              <DemoIcon name="snapshot" />Сохранить кадр
            </button>
          </div>
        </div>
        <DemoTimeline
          v-if="(viewMode === 'tile' || focused) && primaryId"
          :time="time"
          :playing="shouldPlay"
          :speed="speed"
          @seek="seek"
          @toggle="togglePlayback"
          @speed="cycleSpeed"
        />
      </template>
      <div class="demo-app-status">
        <span>{{ notice || 'Попробуйте переключить камеры, открыть архив или изменить вид.' }}</span
        ><span class="demo-app-status-name">{{ appName }}</span>
      </div>
      <span class="sr-only" role="status" aria-live="polite">{{ notice }}</span>
    </div>
  </div>
</template>
<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    appName?: string;
    logoUrl?: string;
    logoIsWordmark?: boolean;
    primaryColor?: string;
    initialTheme?: 'light' | 'dark';
    whiteLabel?: boolean;
  }>(),
  { appName: 'Robotics Vision', logoUrl: '', initialTheme: 'dark', whiteLabel: false },
);
const initials = computed(() =>
  props.appName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase(),
);
// Match the product: the custom accent applies to the light theme.
const brandStyle = computed(() =>
  props.primaryColor && /^#[0-9a-f]{6}$/i.test(props.primaryColor) && theme.value === 'light'
    ? { '--vs-primary': props.primaryColor, '--vs-bg-selected': `${props.primaryColor}22` }
    : {},
);
// Product UI adapted from VideoSurvFront: AppSidebar, HomeToolbar,
// HomeCameraTileGrid and PlaybackTimelineBar. Playback uses local demo files.
type DemoPage = 'cameras' | 'recordings' | 'settings';
type Camera = {
  id: string;
  name: string;
  defaultName: string;
  group: string;
  groupLabel: string;
  enabled: boolean;
  src: string;
  poster: string;
};
type DemoPlayerHandle = { snapshot: () => void };
const root = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
const page = ref<DemoPage>('cameras');
const mode = ref<'live' | 'archive'>('live');
const theme = ref(props.initialTheme);
const fit = ref('contain');
const viewMode = ref<'tile' | 'list'>('tile');
const filter = ref<'all' | 'online' | 'offline'>('all');
const columns = ref(2);
const groupIds = ref<string[]>([]);
const selectedIds = ref<string[]>([]);
const selectionMode = ref(false);
const selectionApplied = ref(false);
const focused = ref<string | null>(null);
const archiveOpen = ref(false);
const time = ref(0);
const speed = ref(1);
const seekVersion = ref(0);
const playing = ref(true);
const loaded = ref(false);
const inView = ref(false);
const pageVisible = ref(true);
const expanded = ref(false);
const fullscreen = ref(false);
const notice = ref('');
const playerRefs: Record<string, DemoPlayerHandle> = {};
const cameras = ref<Camera[]>([
  {
    id: '1',
    name: 'Пешеходный переход',
    defaultName: 'Пешеходный переход',
    group: 'street',
    groupLabel: 'Улица',
    enabled: true,
    src: '/demo/crossing.mp4',
    poster: '/demo/crossing.jpg',
  },
  {
    id: '2',
    name: 'Перекрёсток',
    defaultName: 'Перекрёсток',
    group: 'territory',
    groupLabel: 'Территория',
    enabled: true,
    src: '/demo/intersection.mp4',
    poster: '/demo/intersection.jpg',
  },
]);
const groups = [
  { id: 'street', name: 'Улица' },
  { id: 'territory', name: 'Территория' },
];
const navigation: Array<{ id: DemoPage; label: string; icon: string }> = [
  { id: 'cameras', label: 'Главная', icon: 'home' },
  { id: 'recordings', label: 'Записи', icon: 'recordings' },
  { id: 'settings', label: 'Настройки', icon: 'settings' },
];
const pageTitle = computed(() => navigation.find((item) => item.id === page.value)?.label);
const focusedCamera = computed(() => cameras.value.find((camera) => camera.id === focused.value));
const displayed = computed(() =>
  focused.value
    ? cameras.value.filter((camera) => camera.id === focused.value)
    : cameras.value.filter(
        (camera) =>
          (!groupIds.value.length ||
            groupIds.value.includes('all') ||
            groupIds.value.includes(camera.group)) &&
          (filter.value === 'all' ||
            (filter.value === 'online' ? camera.enabled : !camera.enabled)) &&
          (!selectionApplied.value || selectedIds.value.includes(camera.id)),
      ),
);
const shouldPlay = computed(
  () =>
    playing.value &&
    !!primaryId.value &&
    (viewMode.value === 'tile' || !!focused.value) &&
    inView.value &&
    pageVisible.value &&
    (page.value === 'cameras' || (page.value === 'recordings' && archiveOpen.value)),
);
const primaryId = computed(
  () => displayed.value.find((camera) => camera.enabled || mode.value === 'archive')?.id,
);
const timestamp = computed(() => `14:32:${String(Math.floor(time.value)).padStart(2, '0')}`);
function setPlayerRef(id: string, el: unknown) {
  if (el) playerRefs[id] = el as DemoPlayerHandle;
  else delete playerRefs[id];
}
function navigate(next: DemoPage) {
  page.value = next;
  focused.value = null;
  archiveOpen.value = false;
  notice.value = '';
  selectionMode.value = false;
  mode.value = next === 'recordings' ? 'archive' : 'live';
}
function clearSelection() {
  selectionApplied.value = false;
  selectedIds.value = [];
  selectionMode.value = false;
}
function showAll() {
  focused.value = null;
  groupIds.value = [];
  filter.value = 'all';
  clearSelection();
  if (page.value !== 'cameras' && !archiveOpen.value) {
    page.value = 'cameras';
    mode.value = 'live';
  }
}
function openCamera(id: string) {
  focused.value = id;
  if (page.value !== 'recordings') page.value = 'cameras';
  else archiveOpen.value = true;
  selectionMode.value = false;
  notice.value = '';
}
function selectCamera(id: string) {
  if (!selectionMode.value) {
    openCamera(id);
    return;
  }
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter((item) => item !== id)
    : [...selectedIds.value, id];
}
function setSelectionMode(value: boolean) {
  selectionMode.value = value;
  if (value) {
    selectedIds.value = [];
    selectionApplied.value = false;
  }
}
function applySelection() {
  if (selectedIds.value.length) {
    selectionApplied.value = true;
    selectionMode.value = false;
  }
}
function openArchive() {
  archiveOpen.value = true;
  showAll();
  viewMode.value = 'tile';
  mode.value = 'archive';
  seek(0);
  playing.value = true;
}
function seek(value: number) {
  time.value = Math.min(30, Math.max(0, value));
  seekVersion.value++;
  mode.value = 'archive';
}
function goLive() {
  mode.value = 'live';
  time.value = 0;
  seekVersion.value++;
  speed.value = 1;
  playing.value = true;
}
function togglePlayback() {
  if (time.value >= 29.9) {
    time.value = 0;
    seekVersion.value++;
  }
  playing.value = !playing.value;
}
function cycleSpeed() {
  const speeds = [0.5, 1, 2, 4];
  speed.value = speeds[(speeds.indexOf(speed.value) + 1) % speeds.length]!;
}
function onTime(id: string, value: number) {
  if (id === primaryId.value) time.value = Math.min(30, value);
}
function onEnded(id: string) {
  if (id === primaryId.value && mode.value === 'archive') {
    playing.value = false;
    time.value = 30;
  }
}
function restoreName(camera: Camera) {
  if (!camera.name.trim()) camera.name = camera.defaultName;
}
function resetSettings() {
  theme.value = props.initialTheme;
  fit.value = 'contain';
  speed.value = 1;
  cameras.value.forEach((camera) => {
    camera.name = camera.defaultName;
    camera.enabled = true;
  });
  notice.value = 'Настройки демо сброшены.';
}
let oldOverflow = '';
let previousFocus: HTMLElement | null = null;
let observer: IntersectionObserver | undefined;
async function toggleFullscreen() {
  if (fullscreen.value || expanded.value) {
    await closeExpanded();
    return;
  }
  previousFocus = document.activeElement as HTMLElement | null;
  try {
    if (root.value?.requestFullscreen) {
      await root.value.requestFullscreen();
      return;
    }
  } catch {
    /* The inline expanded view works when the browser declines fullscreen. */
  }
  expanded.value = true;
  oldOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  await nextTick();
  closeButton.value?.focus();
}
async function closeExpanded() {
  if (document.fullscreenElement === root.value) await document.exitFullscreen();
  if (expanded.value) {
    expanded.value = false;
    document.body.style.overflow = oldOverflow;
  }
  previousFocus?.focus();
}
async function expandCamera(id: string) {
  openCamera(id);
  await toggleFullscreen();
}
function onFullscreen() {
  fullscreen.value = document.fullscreenElement === root.value;
}
function onVisibility() {
  pageVisible.value = !document.hidden;
}
onMounted(() => {
  playing.value = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  onVisibility();
  observer = new IntersectionObserver(
    (entries) => {
      inView.value = entries.some((entry) => entry.isIntersecting);
      if (inView.value) loaded.value = true;
    },
    { threshold: 0.05 },
  );
  if (root.value) observer.observe(root.value);
  document.addEventListener('visibilitychange', onVisibility);
  document.addEventListener('fullscreenchange', onFullscreen);
});
onBeforeUnmount(() => {
  observer?.disconnect();
  document.removeEventListener('visibilitychange', onVisibility);
  document.removeEventListener('fullscreenchange', onFullscreen);
  if (expanded.value) document.body.style.overflow = oldOverflow;
});
</script>
<style lang="scss">
@use '~/assets/styles/demo/sidebar.scss' as *;
@use '~/assets/styles/demo/application.scss' as *;
</style>
